import type { PrimitiveOperationDerivationOptions } from '@/adapters/primitive/_contract/natural/arithmetic';
import type { NaturalTrace } from '@/adapters/primitive/_contract/natural/machine';
import type { NaturalDivisionCycle, NaturalDivisionTrace } from '@/adapters/primitive/_contract/trace';
export type { NaturalDivisionCycle, NaturalDivisionTrace } from '@/adapters/primitive/_contract/trace';
import { deriveNaturalTrace } from '@/adapters/primitive/_pure/model/natural/machine';

export const DEFAULT_RUNAWAY_DIVISION_CYCLE_BUDGET = 12;

function wheelValue(trace: NaturalTrace, role: 'difference' | 'sum'): number {
    const finalState = trace.points.at(-1)?.nextState ?? trace.initialState;
    const wheel = finalState.wheels.find((candidate) => candidate.role === role);
    if (wheel === undefined) throw new Error(`Natural division trace is missing ${role}.`);
    return wheel.bands.reduce(
        (value, band) => value + band.countedSlots.length * 10 ** band.power,
        0,
    );
}

export function deriveNaturalDivisionTrace(args: {
    dividend: number;
    divisor: number;
    radix: number;
}, options: PrimitiveOperationDerivationOptions = {}): NaturalDivisionTrace {
    if (args.divisor === 0 && !options.allowExoticTerminalStates) {
        throw new RangeError('Natural division requires a nonzero Divisor.');
    }

    const initializationTrace = deriveNaturalTrace({
        operation: 'addition',
        radix: args.radix,
        operandOne: args.dividend,
        operandTwo: 0,
        operandSchedulingMode: 'grouped',
    });

    const cycleBudget = options.runawayDivisionCycleBudget ?? DEFAULT_RUNAWAY_DIVISION_CYCLE_BUDGET;
    const cycles: NaturalDivisionCycle[] = [];
    let remainder = args.dividend;
    let quotient = 0;
    let budgetExhausted = false;

    while (remainder >= args.divisor && !budgetExhausted) {
        const subtractionTrace = deriveNaturalTrace({
            operation: 'subtraction',
            radix: args.radix,
            operandOne: remainder,
            operandTwo: args.divisor,
            operandSchedulingMode: 'grouped',
        }, { preResultInitialization: 'resident' });
        const quotientIncrementTrace = deriveNaturalTrace({
            operation: 'addition',
            radix: args.radix,
            operandOne: quotient,
            operandTwo: 1,
            operandSchedulingMode: 'grouped',
        }, { preResultInitialization: 'resident' });
        const remainderAfter = wheelValue(subtractionTrace, 'difference');
        const quotientAfter = wheelValue(quotientIncrementTrace, 'sum');
        cycles.push({
            cycleIndex: cycles.length + 1,
            remainderBefore: remainder,
            remainderAfter,
            quotientBefore: quotient,
            quotientAfter,
            subtractionTrace,
            quotientIncrementTrace,
        });
        remainder = remainderAfter;
        quotient = quotientAfter;
        // A cycle that does not move the Remainder never will, so the budget only bites once it diverges.
        if (remainderAfter === cycles[cycles.length - 1].remainderBefore) {
            budgetExhausted = cycles.length >= cycleBudget;
        }
    }

    return {
        dividend: args.dividend,
        divisor: args.divisor,
        initializationTrace,
        cycles,
        quotient,
        remainder,
        outcome: budgetExhausted
            ? {
                outcomeKind: 'failed',
                failureKind: 'runaway-zero-divisor',
                cutoff: { cutoffKind: 'cycle-budget', limit: cycleBudget },
            }
            : { outcomeKind: 'completed' },
    };
}